// This component has a V2 because of data driven consultation, other consultation will use this component when migrated.
import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { productsCategories } from 'constants/products';
import {
  STYLING_GEL_CONSULTATION_QUESTION_SET_KEY,
  SUPPLEMENTS_CONSULTATION_QUESTION_SET_KEY,
} from '../constants';

import ConsultationHero from '../Scenes/ConsultationHero';
import StartStylingGelInterstitial from '../Scenes/StartStylingGelInterstitial';
import StylistHero from '../Scenes/StylistHero';

import * as stylistActions from 'dux/stylist/actions';
import { getIsAuthenticated } from 'dux/auth/selectors';
import {
  getFirstHaircareConsultationQuestionRoute,
  getFirstSkincareConsultationQuestionRoute,
  hasBufferedAnswers,
  userShouldHaveSupplementsQuestion,
} from 'dux/consultation/selectors';
import { getIsProseStylist, getStylistAvatar, getStylistName } from 'dux/stylist/selectors';
import * as userSelectors from 'dux/user/selectors';
import { bufferedAnswersActions } from 'dux/consultation/slice';

import hairWelcome from '../questions/hair/hairWelcome';
import skinWelcome from '../questions/skin/skinWelcome';
import stylingGelWelcome from '../questions/stylingGelWelcome';
import supplementsConsultationWelcome from '../questions/supplementsConsultationWelcome';

interface HeroContainerV2Props {
  consultationCategory: (typeof productsCategories)[keyof typeof productsCategories];
  consultationSubsetCategory:
    | typeof STYLING_GEL_CONSULTATION_QUESTION_SET_KEY
    | typeof SUPPLEMENTS_CONSULTATION_QUESTION_SET_KEY;
  routeParams: URLSearchParams;
}

const HeroContainerV2 = ({
  consultationCategory,
  consultationSubsetCategory,
  routeParams,
}: HeroContainerV2Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = useAppSelector(getIsAuthenticated);
  const firstHaircareConsultationQuestionRoute = useAppSelector(
    getFirstHaircareConsultationQuestionRoute,
  );
  const firstSkincareConsultationQuestionRoute = useAppSelector(
    getFirstSkincareConsultationQuestionRoute,
  );
  // @ts-expect-error - selector not migrated yet
  const isProseStylist = useAppSelector(getIsProseStylist);
  // @ts-expect-error - selector not migrated yet
  const stylistAvatar = useAppSelector(getStylistAvatar);
  // @ts-expect-error - selector not migrated yet
  const stylistName = useAppSelector(getStylistName);
  const showSupplementsWelcome = useAppSelector((state) =>
    userShouldHaveSupplementsQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
    }),
  );
  const hasStoredAnswers = useAppSelector(hasBufferedAnswers);
  const userFirstName = useAppSelector(userSelectors.getUserFirstName);

  const searchParams = new URLSearchParams(location.search);
  const returningPropectParams = Boolean(searchParams.get('is-returning'));

  const getContentByCategoryAndSubCategory = () => {
    if (consultationSubsetCategory === STYLING_GEL_CONSULTATION_QUESTION_SET_KEY) {
      return stylingGelWelcome;
    }
    if (consultationSubsetCategory === SUPPLEMENTS_CONSULTATION_QUESTION_SET_KEY) {
      return supplementsConsultationWelcome;
    }
    if (consultationCategory === productsCategories.SKINCARE) {
      return skinWelcome;
    }
    return hairWelcome;
  };

  const onNext = () => {
    let nextQuestionRoute = firstHaircareConsultationQuestionRoute;
    if (consultationCategory === productsCategories.SKINCARE) {
      nextQuestionRoute = firstSkincareConsultationQuestionRoute;
    }
    if (consultationSubsetCategory === SUPPLEMENTS_CONSULTATION_QUESTION_SET_KEY) {
      nextQuestionRoute =
        '/consultation/haircare/my-hair/genetic-hair-loss?questionSet=supplements';
    }
    // Styling-Gel-only consultation
    if (consultationSubsetCategory === STYLING_GEL_CONSULTATION_QUESTION_SET_KEY) {
      nextQuestionRoute = '/consultation/haircare/my-hair/texture?questionSet=styling-gel';
    }

    navigate(nextQuestionRoute);
  };

  useEffect(() => {
    dispatch(stylistActions.fetchAndTrySaveStylist());
  }, []);

  const handleResetAnwserBuffer = () => {
    dispatch(bufferedAnswersActions.clearAnswerBuffer());
    onNext();
  };

  const handleNext = () => {
    if (returningPropectParams) {
      navigate(`/consultation/${consultationCategory}/my-lifestyle/zipcode`);
    } else {
      onNext();
    }
  };

  if (consultationSubsetCategory === STYLING_GEL_CONSULTATION_QUESTION_SET_KEY) {
    return <StartStylingGelInterstitial onNext={onNext} />;
  }

  return isProseStylist ? (
    <ConsultationHero
      hasStoredAnswers={hasStoredAnswers}
      isReturning={returningPropectParams}
      onClick={handleNext}
      onResetStoredData={handleResetAnwserBuffer}
      progressBarIndex={0}
      routeParams={routeParams}
      showSignin={showSupplementsWelcome ? false : !isAuthenticated && !hasStoredAnswers}
      userFirstName={userFirstName}
      // @ts-expect-error - consultation hero not migrated to TS
      variant={getContentByCategoryAndSubCategory()}
    />
  ) : (
    <StylistHero avatar={stylistAvatar!} name={stylistName!} onClick={onNext} />
  );
};

export default HeroContainerV2;
